import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Sparkles, TriangleAlert, X } from "lucide-react";
import { useLicenseStatus } from "../../helpers/useLicenseStatus";
import { createPortalSession } from "../../endpoints/stripe/create-portal-session_POST.schema";
import { trackPaymentBannerClick, trackTrialBannerSeen, trackTrialEndedSeen } from "../../helpers/analytics";
import { limitsFor } from "../../helpers/entitlements";

/** Jours restants d'essai à partir desquels le bandeau de fin d'essai s'affiche (D10 : J-3 à J-1). */
export const SEUIL_BANDEAU_ESSAI = 3;

/**
 * Bandeau d'impayé (D8) : sur toutes les pages, tant que Stripe relance le paiement.
 * Le bouton ouvre le portail Stripe pour changer de carte.
 */
export const BandeauImpaye = () => {
  const { data } = useLicenseStatus();
  const { pathname } = useLocation();
  const [attente, setAttente] = useState(false);
  if (data?.etat !== "impaye") return null;

  const ouvrirPortail = async () => {
    trackPaymentBannerClick(pathname);
    setAttente(true);
    try {
      const r = await createPortalSession();
      if ("url" in r && r.url) { window.location.assign(r.url); return; }
    } catch { /* repli ci-dessous */ }
    window.location.assign("/compte/");
  };

  return (
    <div className="km-band km-band--warn" role="status">
      <TriangleAlert aria-hidden="true" />
      <p><strong>Le paiement de votre abonnement n'a pas abouti.</strong> Mettez à jour votre carte pour garder l'analyse IA, l'historique complet et vos alertes.</p>
      <button type="button" className="km-btn" onClick={ouvrirPortail} disabled={attente}>Mettre à jour ma carte</button>
    </div>
  );
};

/**
 * Moins de 24 h d'essai : « se termine aujourd'hui à 21 h 05 » ou « demain à 3 h 40 » (aperçu de 24 h
 * commencé la nuit), heure du visiteur ; sans date lisible, « aujourd'hui ».
 */
export function finDuJour(trialEndsAt: string | null | undefined, now: Date = new Date()): string {
  const fin = trialEndsAt ? new Date(trialEndsAt) : null;
  if (!fin || Number.isNaN(fin.getTime())) return "se termine aujourd'hui";
  const heure = `${fin.getHours()} h ${String(fin.getMinutes()).padStart(2, "0")}`;
  return fin.toDateString() === now.toDateString() ? `se termine aujourd'hui à ${heure}` : `se termine demain à ${heure}`;
}

/**
 * Bandeau de fin d'essai (D10) : sur le tableau de bord, à 3 jours ou moins de la fin.
 * Ce qui reste gratuit est lu dans les limites du serveur, jamais recopié.
 */
export const BandeauFinEssai = () => {
  const { data } = useLicenseStatus();
  const vu = useRef(false);
  const jours = data?.trialDaysRemaining ?? null;
  const visible = data?.etat === "essai" && jours !== null && jours <= SEUIL_BANDEAU_ESSAI;
  // Pendant l'essai, le statut porte les limites Premium : on lit celles du compte gratuit (quotas du serveur).

  useEffect(() => {
    if (visible && jours !== null && !vu.current) {
      vu.current = true;
      trackTrialBannerSeen(jours);
    }
  }, [visible, jours]);

  if (!visible) return null;
  const quand = jours === 0 ? finDuJour(data?.trialEndsAt) : `: encore ${jours} jour${jours > 1 ? "s" : ""}`;

  return (
    <div className="km-band km-band--trial" role="status">
      <Sparkles aria-hidden="true" />
      <p>
        <strong>Essai Premium {quand}.</strong> Ensuite, votre compte reste gratuit : Mes grilles, les 6 listes du soir et nos analyses sur les {limitsFor("free").tirageDepth} derniers tirages.
      </p>
      <Link className="km-band__lien" to="/tarifs/?cta_source=bandeau_fin_essai#comparatif">Voir ce qui change</Link>
    </div>
  );
};

/**
 * Fin d'essai (décision du 22/10/2026, cadrage §7 point 11) : un bandeau dans la page, à la place
 * de la fenêtre. Une seule fois par compte : le serveur dit s'il reste à afficher
 * (trialEndedNotice) et le marque au premier affichage. Jamais sur le premier écran de bienvenue
 * (/home/?bienvenue=1). Ce qui reste gratuit est lu dans les limites du serveur.
 */
export const BandeauEssaiTermine = () => {
  const { data } = useLicenseStatus();
  const { search } = useLocation();
  const bienvenue = new URLSearchParams(search).get("bienvenue") === "1";
  const [ferme, setFerme] = useState(false);
  const marque = useRef(false);
  const visible = data?.trialEndedNotice === true && !bienvenue;
  const offre = data?.sixMonthsOffer === true;

  useEffect(() => {
    if (!visible || marque.current) return;
    marque.current = true;
    trackTrialEndedSeen(offre ? "six_mois" : "aucune");
    void fetch("/_api/license/trial-ended-notice", { method: "POST", credentials: "same-origin" }).catch(() => {});
  }, [visible, offre]);

  if (!visible || ferme) return null;
  const gratuit = limitsFor("free");
  const grilles = gratuit.maxCombinations;

  return (
    <div className="km-band km-band--trial km-band--fin" role="status">
      <Sparkles aria-hidden="true" />
      <p>
        <strong>Votre essai Premium est terminé.</strong> Votre compte reste gratuit : tous les résultats, les 6 listes du soir, Mes grilles ({grilles} grille{grilles > 1 ? "s" : ""}) et nos analyses sur les {gratuit.tirageDepth} derniers tirages. Rien à payer ni à résilier.
      </p>
      <span className="km-band__acts">
        {offre && <a className="km-band__lien" href="/tarifs/?cta_source=fin_essai#six-mois">6 mois de Premium pour 19 €</a>}
        <a className="km-band__lien" href="/tarifs/?cta_source=fin_essai">{offre ? "Toutes les formules" : "Voir les formules"}</a>
        <button type="button" className="km-band__close" aria-label="Fermer" onClick={() => setFerme(true)}><X aria-hidden="true" /></button>
      </span>
    </div>
  );
};
